import { getAchievementsProgress } from '@/api/endpoints/achievements.api'
import type { AchievementsProgressResponse, Candidate } from '@/api/types/generated'
import { createSignal } from 'solid-js'
import { authStore } from './auth.store'

export function computeLevelUp(args: { newLevel: number; storedLevel: number | null }):
  | number
  | null {
  if (args.storedLevel === null) {
    return null
  }
  return args.newLevel > args.storedLevel ? args.newLevel : null
}

const storageKey = (candidateId: number) => `peatch-gamification-level-${candidateId}`

function readStoredLevel(candidateId: number): number | null {
  try {
    const raw = localStorage.getItem(storageKey(candidateId))
    return raw === null ? null : Number(raw)
  } catch {
    return null
  }
}

function writeStoredLevel(candidateId: number, level: number): void {
  try {
    localStorage.setItem(storageKey(candidateId), String(level))
  } catch {
    // Browsers can block localStorage; without a stored level nothing celebrates.
  }
}

function currentCandidateId(): number | null {
  const state = authStore.getState()
  if (state.userType !== 'candidate' || !state.user) {
    return null
  }
  return (state.user as Candidate).id
}

const [progress, setProgress] = createSignal<AchievementsProgressResponse | null>(null)
const [pendingLevelUp, setPendingLevelUp] = createSignal<{ level: number; title: string } | null>(
  null
)

let inFlight = false

async function refresh(): Promise<void> {
  if (inFlight) {
    return
  }
  const candidateId = currentCandidateId()
  if (candidateId === null) {
    return
  }
  inFlight = true
  try {
    const data = await getAchievementsProgress()
    setProgress(data)

    const newLevel = data.xp_progress.current_level
    const storedLevel = readStoredLevel(candidateId)
    const celebrate = computeLevelUp({ newLevel, storedLevel })

    if (storedLevel === null) {
      writeStoredLevel(candidateId, newLevel)
    } else if (celebrate !== null) {
      setPendingLevelUp({ level: celebrate, title: data.xp_progress.level_title })
    }
  } catch {
    // leave previous progress in place
  } finally {
    inFlight = false
  }
}

function clearPendingLevelUp(): void {
  const pending = pendingLevelUp()
  const candidateId = currentCandidateId()
  if (pending && candidateId !== null) {
    writeStoredLevel(candidateId, pending.level)
  }
  setPendingLevelUp(null)
}

function reset(): void {
  setProgress(null)
  setPendingLevelUp(null)
}

export const gamificationStore = {
  progress,
  pendingLevelUp,
  refresh,
  clearPendingLevelUp,
  reset,
}
